import { useCallback, useRef, useEffect } from "react";
import { useAtom } from "jotai";

import { VideoProgressBar } from "@/components/atoms/VideoProgressBar";
import { BrightnessSlider } from "@/components/atoms/BrightnessSlider";
import { SoundButton } from "@/components/atoms/SoundButton";
import { IconButton } from "@/components/atoms/IconButton";
import { useClassNames } from "@/utils/classNames";
import { Icon, StreamingIcons } from "@/components/atoms/Icon";
import { videoPlayerAtom } from "@/constants/atoms";

import styles from "./VideoControlsBottom.module.scss";
import { VideoControlsBottomProps } from "./types";

/**
 * Bottom video player controls molecule that contains the progress bar
 * and brightness slider
 * and sound control
 *
 * @param props - The video controls properties
 * @returns the video controls component
 */
const VideoControlsBottom = ({
  videoRef,
  onSeek,
  className,
}: VideoControlsBottomProps) => {
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);
  const resetControlsTimerRef = useRef(videoPlayerState.resetControlsTimer);

  /** Keep ref up to date with latest resetControlsTimer */
  useEffect(() => {
    resetControlsTimerRef.current = videoPlayerState.resetControlsTimer;
  }, [videoPlayerState.resetControlsTimer]);

  const handleScrubStart = useCallback(() => {
    setVideoPlayerState((prev) => ({ ...prev, isScrubbing: true }));
  }, [setVideoPlayerState]);

  const handleScrubEnd = useCallback(() => {
    /** Reset controls timer to show controls after scrubbing ends */
    resetControlsTimerRef.current?.();
    setVideoPlayerState((prev) => ({ ...prev, isScrubbing: false }));
  }, [setVideoPlayerState]);

  const handleBrightnessDragStart = useCallback(() => {
    setVideoPlayerState((prev) => ({ ...prev, isAdjustingBrightness: true }));
  }, [setVideoPlayerState]);

  const handleBrightnessDragEnd = useCallback(() => {
    setVideoPlayerState((prev) => ({ ...prev, isAdjustingBrightness: false }));
  }, [setVideoPlayerState]);

  const handleSubtitleClick = useCallback(() => {
    setVideoPlayerState((prev) => ({
      ...prev,
      isSubtitleOverlayOpen: true,
    }));
  }, [setVideoPlayerState]);

  const { isScrubbing, isAdjustingBrightness } = videoPlayerState;

  const cn = useClassNames(styles, styles["bottom-video-controls"], className);

  const progressBarWrapperCn = useClassNames(
    styles,
    styles["progress-bar-wrapper"],
    isAdjustingBrightness && styles["hidden"],
  );

  const controlsCn = useClassNames(
    styles,
    styles["controls"],
    (isAdjustingBrightness || isScrubbing) && styles["hidden"],
  );

  const brightnessSliderCn = useClassNames(
    styles,
    styles["controls"],
    isScrubbing && styles["hidden"],
  );

  const rightControlsCn = useClassNames(
    styles,
    styles["controls"],
    styles["right-controls"],
    (isAdjustingBrightness || isScrubbing) && styles["hidden"],
  );

  return (
    <div className={cn}>
      <div className={progressBarWrapperCn}>
        <VideoProgressBar
          videoRef={videoRef}
          onSeek={onSeek}
          onDragStart={handleScrubStart}
          onDragEnd={handleScrubEnd}
          className={styles["progress-bar"]}
        />
      </div>
      <div className={styles["controls-wrapper"]}>
        <div className={controlsCn}>
          <Icon icon={StreamingIcons.MoreEpisodes} />
        </div>
        <div className={rightControlsCn}>
          <BrightnessSlider
            videoRef={videoRef}
            onDragStart={handleBrightnessDragStart}
            onDragEnd={handleBrightnessDragEnd}
            className={brightnessSliderCn}
          />
          <SoundButton className={styles["sound-button"]} />
          <IconButton
            icon={StreamingIcons.Subtitle}
            onClick={handleSubtitleClick}
            ariaLabel="Subtitles"
          />
        </div>
      </div>
    </div>
  );
};

export { VideoControlsBottom };
